import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  Loader2,
  History,
  RotateCcw,
  RefreshCcw,
  Clock,
} from "lucide-react";
import { Button } from "@/components/ui/button";

export default function AdConfigHistory() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/ad-config/history");
      setItems(data);
    } catch (e) {
      toast.error("Failed to load history");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const restore = async (snapshot_at) => {
    if (!confirm("Restore this snapshot? Current config will be snapshotted first.")) return;
    try {
      await api.post(
        `/ad-config/restore/${encodeURIComponent(snapshot_at)}`
      );
      toast.success("Restored");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Restore failed");
    }
  };

  return (
    <div className="p-6 lg:p-10 max-w-4xl">
      <div className="flex items-start justify-between gap-4 mb-8 animate-in-up flex-wrap">
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-primary mb-2">
            Rollback
          </div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-1">
            Ad Config History
          </h1>
          <p className="text-sm text-muted-foreground max-w-xl">
            Every save/reset is snapshotted. Restore any prior version with one click.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={load}
          data-testid="history-refresh-btn"
          className="h-10"
        >
          <RefreshCcw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      {loading ? (
        <div className="text-muted-foreground flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : items.length === 0 ? (
        <div className="bg-card border border-border rounded-2xl p-16 text-center">
          <History className="w-8 h-8 mx-auto text-muted-foreground mb-3" />
          <div className="text-sm font-medium">No snapshots yet</div>
          <div className="text-xs text-muted-foreground mt-1">
            Save an ad-config change to create the first snapshot.
          </div>
        </div>
      ) : (
        <div className="relative border-l-2 border-border ml-2 pl-8 space-y-4 stagger">
          {items.map((s, idx) => (
            <div
              key={s.id}
              data-testid={`history-item-${idx}`}
              className="relative bg-card border border-border rounded-2xl p-5 hover:shadow-md transition-all"
            >
              <span className="absolute -left-[41px] top-6 w-4 h-4 rounded-full bg-primary ring-4 ring-background" />
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="flex items-center gap-2 text-sm font-bold">
                    <Clock className="w-4 h-4 text-muted-foreground" />
                    {new Date(s.snapshot_at).toLocaleString()}
                  </div>
                  <div className="mt-1 text-xs">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-secondary text-secondary-foreground mono">
                      {s.reason}
                    </span>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-4 text-xs text-muted-foreground">
                    <span>
                      Ads:{" "}
                      <span className="font-semibold text-foreground">
                        {s.summary?.enabled ? "on" : "off"}
                      </span>
                    </span>
                    <span>
                      Video ads:{" "}
                      <span className="font-semibold text-foreground">
                        {s.summary?.video_ads_enabled ? "on" : "off"}
                      </span>
                    </span>
                    <span>
                      Rules:{" "}
                      <span className="font-semibold text-foreground">
                        {s.summary?.video_rules ?? 0}
                      </span>
                    </span>
                    <span>
                      Placements active:{" "}
                      <span className="font-semibold text-foreground">
                        {s.summary?.placements_enabled ?? 0}
                      </span>
                    </span>
                  </div>
                </div>
                <Button
                  onClick={() => restore(s.snapshot_at)}
                  data-testid={`history-restore-${idx}`}
                  variant="outline"
                  className="h-9"
                >
                  <RotateCcw className="w-4 h-4 mr-2" /> Restore
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
